import {
  Box,
  Select,
  Typography,
  InputAdornment,
  InputLabel,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { ConnectingAirportsOutlined } from "@mui/icons-material";

export const PnpSelect = ({
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  width,
  options,
  name,
  errors,
  validation,
  wrapperSx,
  isDisabled,
  mode,
  customeonchange,
  customsx
}) => {
console.log("PnpSelect", name, value, options, errors);

  // ONLY handle default value for specific fields that need it
  useEffect(() => {
    // Only apply default value logic to specific fields like match_week
    const fieldsWithDefaults = ['match_week'];
    
    if (fieldsWithDefaults.includes(name)) {
      // Set default value when options are loaded and no value is set
      if (!value && defaultValue && options && options.length > 0) {
        const defaultOption = options.find(option => option.id === defaultValue);
        if (defaultOption) {
          // Use setTimeout to ensure the onChange is called after component initialization
          setTimeout(() => {
            onChange(name, defaultValue);
            if (customeonchange) {
              customeonchange(defaultValue);
            }
          }, 0);
        }
      }
    }
  }, [defaultValue, options, name, value, onChange, customeonchange]);

  const onSelectChange = (event, newValue) => {
    if (newValue) {
      if(customeonchange){
        customeonchange(newValue.id)
      }
      return onChange(name, newValue.id);
    }
    // ONLY add special clearing logic for fields that need defaults
    else {
      const fieldsWithDefaults = ['match_week'];
      if (fieldsWithDefaults.includes(name) && defaultValue) {
        onChange(name, defaultValue);
        if (customeonchange) {
          customeonchange(defaultValue);
        }
      }
      // For all other fields, keep original behavior (no special clearing logic)
    }
  };

  // Special logic for season_year field only
  const getDisplayValue = () => {
     if (name === 'is_active') {
    if (value !== undefined && value !== null && options && options.length > 0) {
      const foundOption = options.find((option) => option && option.id === value);
      if (foundOption) {
        return foundOption;
      }
    }
    return null;
  }
  
    if (name === 'season_year') {
      const currentYear = new Date().getFullYear();
      
      // If no value is selected, show current year as placeholder/default
      if (!value) {
        return { id: currentYear, name: currentYear.toString() };
      }
      
      // Check if selected value exists in options
      const foundInOptions = options.find((option) => option && option.id === value);
      if (foundInOptions) {
        return foundInOptions;
      }
      
      // If selected value is current year (not in options), create display object
      if (value === currentYear) {
        return { id: currentYear, name: currentYear.toString() };
      }
      
      // Return null if nothing matches
      return null;
    }
    
    // CONDITIONAL logic for fields that need default value display
    const fieldsWithDefaults = ['match_week'];
    
    if (fieldsWithDefaults.includes(name)) {
      // Enhanced logic for match_week - show default value when no value is set
      const currentValue = value || defaultValue;
      
      if (currentValue && options && options.length > 0) {
        const foundOption = options.find((option) => option && option.id === currentValue);
        if (foundOption) {
          return foundOption;
        }
      }
      
      // Return null if no match found
      return null;
    }
    
    // EXACT original behavior for ALL other fields - no changes
    return options.find((option) => option.id === value);
  };

  // Filter options only for season_year, keep original for others
  const getFilteredOptions = () => {
    if (name === 'season_year') {
      return options.filter(option => option && option.name !== undefined && option.name !== null);
    }
    // EXACT original behavior - no filtering for other fields
    return options;
  };

  // Get option label with special handling for season_year
  const getOptionLabel = (option) => {
    if (name === 'season_year') {
      if (!option || option.name === undefined || option.name === null) {
        return '';
      }
      return option.name.toString();
    }
    // EXACT original behavior - no null checks for other fields
    return option.name.toString();
  };

  return (
    <Box sx={wrapperSx}>
      <InputLabel htmlFor={name} component="div" sx={{ fontWeight: 600, fontSize: 16, color: '#000', error: errors?.status }}>
        {label}
        {validation?.required && <Typography component="span" sx={{ color: 'red', marginLeft: '4px' }}>*</Typography>}
      </InputLabel>      

      <Autocomplete
        disablePortal
        id="combo-box-demo"
        value={getDisplayValue()}
        options={getFilteredOptions()}
        getOptionLabel={getOptionLabel}
        // Only add isOptionEqualToValue for season_year
        {...(name === 'season_year' && {
          isOptionEqualToValue: (option, value) => {
            if (!option || !value) return false;
            return option.id === value.id;
          }
        })}
        onChange={onSelectChange}
        sx={{
          width: width || "auto",
          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
            height: '100%',
            padding: 0
          },
          pointerEvents: isDisabled || mode === "view" ? "none" : "all",
          ...customsx
        }}
        renderInput={(params) => {
          // Special renderInput only for season_year
          if (name === 'season_year') {
            const currentYear = new Date().getFullYear();
            const displayValue = value || currentYear;
            
            return (
              <TextField 
                {...params}
                InputProps={{
                  ...params.InputProps,
                  value: displayValue.toString(),
                }}
              />
            );
          }
          
          // Original behavior for other fields
          return <TextField {...params} />;
        }}
        // Handle clearing of input only for season_year
        {...(name === 'season_year' && {
          onInputChange: (event, newInputValue, reason) => {
            if (reason === 'clear') {
              // Reset to current year when cleared
              const currentYear = new Date().getFullYear();
              onChange(name, currentYear);
            }
          }
        })}
      />
      {errors?.status && (
        <FormHelperText error={errors.status}>
          {_.capitalize(errors?.message)}
        </FormHelperText>
      )}
    </Box>
  );
};